import {NavigateFunction} from "react-router-dom";
import {getUserTokenOrRedirect} from "../../shared/services/auth.service";

/** The word typed to confirm Delete account (the iOS and Android apps use the same). */
export const DELETE_CONFIRMATION_WORD = 'DELETE';

/** Days between closing an account and erasing its personal details. */
export const RECOVERY_DAYS = 90;

/** The field holds the word, in capitals, give or take spaces around it. */
export const isDeleteConfirmed = (typed: string) => typed.trim() === DELETE_CONFIRMATION_WORD;

export const closeAccountCopy = {
  reauthWrongPassword: "That password isn't right.",
  reauthWrongCode: "That code isn't right.",
  reauthMismatch: "That's a different account. Confirm with the account you're signed in with.",
  reauthTooMany: 'Too many tries. Wait a few minutes and try again.',
  reauthUnavailable: "We can't confirm it's you here. Sign out, sign in again, then try once more.",
  reauthExpired: "Please confirm it's you again.",
  genericError: "Couldn't delete your account. Try again.",
  doneTitle: 'Account closed',
  doneBody: (date: string) =>
    `You're signed out. Your account will be permanently deleted on ${date}. Until then, SavaQuan support can restore it: savaquan.info@gmail.com.`,
  closedDisabled: 'This account is closed. To restore it, email savaquan.info@gmail.com.',
};

const MONTH_DAY_YEAR = new Intl.DateTimeFormat('en-US', {month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC'});

/**
 * A UTC calendar day as "January 9, 2027". Takes the API's eraseOn
 * ("2027-01-09") or an ISO timestamp, whose UTC date it shows: the erase date
 * is the same day on every device, wherever the reader is.
 */
export const formatEraseOn = (value: string): string => {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = day ? new Date(Date.UTC(+day[1], +day[2] - 1, +day[3])) : new Date(value);
  return Number.isNaN(date.getTime()) ? value : MONTH_DAY_YEAR.format(date);
};

/** The erase date an account closed now would get: today + 90 days, as a UTC calendar day. */
export const previewEraseOn = (now: Date = new Date()): string =>
  formatEraseOn(new Date(now.getTime() + RECOVERY_DAYS * 24 * 60 * 60 * 1000).toISOString());

export type ReauthMethod = 'password' | 'phone' | 'google' | 'unavailable';

const METHOD_BY_PROVIDER: Record<string, ReauthMethod> = {
  'password': 'password',
  'phone': 'phone',
  'google.com': 'google',
};

/**
 * How to confirm it's the account holder: the way this session signed in, or
 * else the first linked method this app can run (a custom-token session from
 * the native app, or Apple, has nothing to repeat here).
 */
export const reauthMethod = (signInProvider: string | null | undefined, linkedProviders: string[]): ReauthMethod => {
  if (signInProvider && METHOD_BY_PROVIDER[signInProvider]) {
    return METHOD_BY_PROVIDER[signInProvider];
  }
  const linked = Object.keys(METHOD_BY_PROVIDER).find(provider => linkedProviders.includes(provider));
  return linked ? METHOD_BY_PROVIDER[linked] : 'unavailable';
};

/** What to say when a re-authentication fails; null when the person cancelled it themselves. */
export const reauthErrorText = (error: unknown): string | null => {
  switch ((error as {code?: string} | null)?.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null;
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return closeAccountCopy.reauthWrongPassword;
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
    case 'auth/code-expired':
      return closeAccountCopy.reauthWrongCode;
    case 'auth/user-mismatch':
      return closeAccountCopy.reauthMismatch;
    case 'auth/too-many-requests':
    case 'auth/quota-exceeded':
      return closeAccountCopy.reauthTooMany;
    default:
      return closeAccountCopy.genericError;
  }
};

export interface ClosedAccount {
  closedAt: string;
  eraseAfter: string;
  /** The UTC calendar day the personal details are erased, as "2027-01-09". */
  eraseOn: string;
}

/**
 * A refused close. A 409's message is the server's reason, for the person
 * to read; anything else says to try again. code is the server's error code
 * (reauth_required, sole_owner) when it sent one.
 */
export class CloseAccountError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'CloseAccountError';
    this.status = status;
    this.code = code;
  }
}

export const isCloseAccountError = (e: unknown): e is CloseAccountError =>
  e instanceof CloseAccountError || (e as {name?: string} | null)?.name === 'CloseAccountError';

// POST users/me/close: closes the signed-in person's account now; the server
// erases its personal details on eraseOn. Closing an account that is already
// closed answers with its existing dates.
export async function closeMyAccount(navigate: NavigateFunction): Promise<ClosedAccount> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/users/me/close`,
    {method: 'POST', headers: {Authorization: `Bearer ${token}`}});
  if (response.ok) {
    return await response.json() as ClosedAccount;
  }
  const body = await response.json().catch(() => null) as {message?: string; code?: string} | null;
  const message = response.status === 409 && body?.message ? body.message : closeAccountCopy.genericError;
  throw new CloseAccountError(response.status, body?.code ?? undefined, message);
}

// The sign-in page shows the Account closed notice once after closing. It
// travels in sessionStorage because the sign-in page is another route,
// reached only after signing out.
const CLOSED_NOTICE_KEY = 'savaquan.accountClosedEraseOn';

export const rememberClosedNotice = (eraseOn: string) => {
  try {
    sessionStorage.setItem(CLOSED_NOTICE_KEY, eraseOn);
  } catch {
    // Storage can be off (private mode); the account is closed all the same.
  }
};

export const readClosedNotice = (): string | null => {
  try {
    return sessionStorage.getItem(CLOSED_NOTICE_KEY);
  } catch {
    return null;
  }
};

export const forgetClosedNotice = () => {
  try {
    sessionStorage.removeItem(CLOSED_NOTICE_KEY);
  } catch {
    // Nothing was stored.
  }
};
